import React, { useMemo } from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { ButtonProps } from '@cp/ds/src/components/button/model';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Button } from '@cp/ds/src/components/button';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { ClientType } from '@common/model/client/clientType';
import { useClientProfile } from 'src/entities/client';
import { useCheckPatronageCompleted, useShowPatronageHints } from 'src/features/client/current/patronage';
import { RespondToVacancyDeps, ResponseButton } from 'src/features/catalogItem/response';
import { withSubscriptionTooltip } from 'src/features/billing';
import { useTrackEvent, withTrackClick } from 'src/shared/tracking';
import { CatalogEvents } from 'src/features/catalog';
import { mapCatalogItemToClientSelection, useAuth, withAuth } from 'src/features/auth';
import { useCatalogItem } from 'src/entities/catalog';
import { _experimentalComponentBuilder as componentBuilder } from 'src/shared/componentBuilder';
import { withConnect } from 'src/features/catalogItem/connect';
import { useInviteSpecialist } from 'src/widgets/resume/model';
import { CatalogItemActionsProps } from './model';
import { Favorite } from './favorite';

export * from './model';

const ResponseButtonWithSubscription = withSubscriptionTooltip(ResponseButton);

const ConnectButton = componentBuilder(Button)
  .compose(withConnect)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemQuestion)
  .compose(withAuth)
  .build();

const InviteButton = componentBuilder(Button).composeWithArgs(withTrackClick, CatalogEvents.ItemInvite).compose(withAuth).build();

const WriteButton = componentBuilder(Button).compose(withConnect).composeWithArgs(withTrackClick, CatalogEvents.ItemQuestion).compose(withAuth).build();

const resumeButtonProps: ButtonProps = {
  color: 'gray',
  size: 'normal',
  sx: { flex: '1 1 0', px: 2 },
  variant: 'primary',
};

const writeButtonProps: ButtonProps = {
  color: 'gray',
  size: 'normal',
  sx: { flex: '1 1 0', px: 2 },
  variant: 'secondary',
};

export const CatalogItemActions: React.FC<CatalogItemActionsProps> = ({ sx, id: customId, catalogType: customCatalogType }) => {
  const auth = useAuth();
  const theme = useTheme();
  const track = useTrackEvent();
  const { id: actionId, catalogType: actionCatalogType } = useCatalogItem();
  const id = customId ?? actionId ?? 0;
  const catalogType = customCatalogType ?? actionCatalogType ?? CatalogItemType.Resume;
  const clientProfile = useClientProfile();
  const { data: catalogItem, isLoading } = coreCatalogItemApi.endpoints.getById.useQuery(
    { id: id as NonNullable<typeof id> },
    { skip: !id },
  );
  const inviteSpecialist = useInviteSpecialist(catalogItem);

  const type = catalogType ?? catalogItem?.type ?? CatalogItemType.Resume;
  const isResume = type === CatalogItemType.Resume;

  const buttonsProps: Omit<ButtonProps, 'variant'> = {
    sx: isResume ? {} : { flex: { xs: '1 1 50%', [MOBILE_BREAKPOINT_THRESHOLD]: 'initial' }, whiteSpace: 'nowrap' },
    disabled: clientProfile?.type ? clientTypeToSuitableCatalogItemType(clientProfile.type) !== type : false,
  };

  const checkPatronageCompleted = useCheckPatronageCompleted();
  const showPatronageHints = useShowPatronageHints();
  const respondToVacancyDeps = useMemo(
    (): RespondToVacancyDeps => ({
      onNeedLogin: () => (catalogItem ? auth({ careerId: catalogItem.careerId, clientType: ClientType.Specialist }) : auth()),
      patronage: {
        check: checkPatronageCompleted,
        action: showPatronageHints,
      },
    }),
    [auth, checkPatronageCompleted, showPatronageHints, catalogItem],
  );

  let content: React.ReactNode = null;

  if (isLoading) {
    content = (
      <>
        <Skeleton height={40} width={120} />
        <Skeleton height={40} width={120} />
      </>
    );
  } else if (catalogItem) {
    content = isResume ? (
      <>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ maxWidth: '480px', width: '100%', px: { xs: '12px', md: 0 }, mt: { xs: 0, md: '20px' } }}
        >
          <InviteButton {...resumeButtonProps} onClick={inviteSpecialist}>
            Позвать
          </InviteButton>
          <WriteButton {...writeButtonProps} catalogItemId={catalogItem.id}>
            Написать
          </WriteButton>
        </Stack>
        {catalogItem.clientProfile && (
          <Favorite
            clientSelection={mapCatalogItemToClientSelection(catalogItem)}
            color="gray ultra light"
            {...buttonsProps}
            authorId={catalogItem.clientProfile.id}
          />
        )}
      </>
    ) : (
      <>
        <ResponseButtonWithSubscription
          {...buttonsProps}
          deps={respondToVacancyDeps}
          onClickCapture={() => track(CatalogEvents.ItemResponse())}
          request={{ catalogItem, responder: clientProfile }}
          sx={buttonsProps.sx}
          tooltipText="Чтобы откликаться и видеть контакты необходимо"
        >
          Я могу
        </ResponseButtonWithSubscription>
        <ConnectButton
          color="gray"
          {...buttonsProps}
          catalogItemId={catalogItem.id}
          sx={mergeSx(buttonsProps.sx, { backgroundColor: theme.palette.white })}
          variant="secondary"
        >
          Задать вопрос
        </ConnectButton>
      </>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <Stack alignItems="center" direction="row" spacing={1.5} sx={sx}>
      {content}
    </Stack>
  );
};
